import { useEffect, useState, type FormEvent } from "react";
import { api, navigate, safeNext, setToken } from "./api";
import { Notice } from "./common";
import type { User } from "./types";
export default function Auth({
  register,
  onSuccess,
}: {
  register: boolean;
  onSuccess: (user: User) => void;
}) {
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [code, setCode] = useState(""),
    [invite, setInvite] = useState(
      new URLSearchParams(location.search).get("invite") || "",
    ),
    [remember, setRemember] = useState(false),
    [agreed, setAgreed] = useState(false),
    [busy, setBusy] = useState(false),
    [sending, setSending] = useState(false),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [demo, setDemo] = useState(false);
  const next = safeNext(new URLSearchParams(location.search).get("next"));
  useEffect(() => {
    fetch("/__preview__/info")
      .then((r) => (r.ok ? r.json() : Promise.resolve({ mode: "" })))
      .then((v) => setDemo(v.mode === "sandbox"))
      .catch(() => {});
  }, []);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const data = await api<{ token: string; user: User }>(
        register ? "/auth/register" : "/auth/login",
        {
          email,
          password,
          verifyCode: code,
          inviteCode: invite,
          rememberMe: remember,
        },
      );
      if (!data.token) throw new Error("登录未返回有效凭据");
      setToken(data.token, remember);
      onSuccess(data.user);
      navigate(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="auth-page">
      <button className="brand-button" onClick={() => navigate("/")}>
        <img src="/pandai-wordmark.png" alt="PandAI" width={1942} height={809} />
      </button>
      <section className="auth-card">
        <span className="eyebrow">PANDAI ACCOUNT</span>
        <h1>{register ? "创建账号" : "欢迎回来"}</h1>
        <p>用一个账号，管理你的购买记录与交付内容。</p>
        {demo && (
          <Notice kind="info">
            演示账号：preview@example.test
            <br />
            密码：Preview-only-2026
          </Notice>
        )}
        <form onSubmit={submit}>
          <label>
            邮箱
            <input
              autoComplete="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>
          <label>
            密码
            <input
              autoComplete={register ? "new-password" : "current-password"}
              type="password"
              minLength={register ? 8 : 1}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={register ? "至少 8 位字符" : "请输入密码"}
            />
          </label>
          {register && (
            <>
              <label>
                邮箱验证码
                <div className="field-row">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <button
                    type="button"
                    className="button secondary"
                    disabled={sending || !email}
                    onClick={async () => {
                      setSending(true);
                      setError("");
                      try {
                        await api("/auth/send-register-code", { email });
                        setMessage("验证码已发送，请查收邮件");
                      } catch (e) {
                        setError((e as Error).message);
                      } finally {
                        setSending(false);
                      }
                    }}
                  >
                    {sending ? "正在发送" : "获取验证码"}
                  </button>
                </div>
              </label>
              <label>
                邀请码 <span className="muted">（选填）</span>
                <input
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                />
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  required
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  我已阅读并同意{" "}
                  <a href="/terms-of-service" target="_blank">
                    服务条款
                  </a>{" "}
                  和{" "}
                  <a href="/privacy-policy" target="_blank">
                    隐私政策
                  </a>
                </span>
              </label>
            </>
          )}
          {!register && (
            <label className="check">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              在此设备保持登录
            </label>
          )}
          <Notice>{error}</Notice>
          <Notice kind="success">{message}</Notice>
          <button
            className="button primary full"
            disabled={busy || (register && !agreed)}
          >
            {busy ? "请稍候…" : register ? "注册账号" : "登录"}
          </button>
        </form>
        <p className="auth-switch">
          {register ? "已有账号？" : "还没有账号？"}
          <button
            onClick={() =>
              navigate(
                (register ? "/login" : "/register") +
                  "?next=" +
                  encodeURIComponent(next),
              )
            }
          >
            {register ? "立即登录" : "创建账号"}
          </button>
        </p>
      </section>
      <p className="auth-footer">PandAI · pandai.shop</p>
    </div>
  );
}
